/**
 * Pre-built reports — metadata + thin run wrapper around the purpose-built
 * endpoints on `service-reporting-api` (`PreBuiltReportsController`, mounted
 * at `/v1/reports/*`).
 *
 * Previously this module advertised v1-style `reportKey`s like
 * `audit.compliance.v1` and pages routed them through the generic
 * `POST /v1/reporting/run` engine. That engine's `REPORT_KEYS` registry does
 * not know those keys, so every call 404'd and the UI permanently rendered
 * "Report not yet available." The 10 purpose-built endpoints (one per slug)
 * exist and return real data — `runPreBuiltReport` below routes there
 * instead.
 *
 * The purpose-built endpoints have no server-side CSV/JSON/PDF export — the
 * `ReportShell` and per-report pages handle that client-side via
 * `lib/csv.ts::jsonToCsv` + `downloadBlob`.
 */
import { env } from '../env';

import { request } from './client';

const apiBase = (): string => env.serviceReportingApiBaseUrl;

export interface PreBuiltReport {
  readonly slug: string;
  readonly reportKey: string;
  readonly title: string;
  readonly description: string;
  readonly category: string;
}

export const PREBUILT_REPORTS: readonly PreBuiltReport[] = [
  {
    slug: 'daily-transactions',
    reportKey: 'daily.transactions.summary.v1',
    title: 'Daily Transaction Report',
    description:
      'End-of-day transactions across mail, financial services, and government services for one operator.',
    category: 'Operations',
  },
  {
    slug: 'delivery-sla',
    reportKey: 'delivery.sla.v1',
    title: 'Delivery SLA Report',
    description: 'On-time vs late delivery breakdown, exception reasons, and route-level SLA.',
    category: 'Delivery',
  },
  {
    slug: 'fleet-ops',
    reportKey: 'fleet.operations.v1',
    title: 'Fleet Operations Report',
    description: 'Vehicle utilisation, fuel cost, maintenance flags, and driver assignment density.',
    category: 'Delivery',
  },
  {
    slug: 'kyc-compliance',
    reportKey: 'kyc.compliance.v1',
    title: 'KYC Compliance Report',
    description: 'Verification volumes, watchlist hits, SAR queue health, and NDPR compliance flags.',
    category: 'Compliance',
  },
  {
    slug: 'audit-compliance',
    reportKey: 'audit.compliance.v1',
    title: 'Audit & Compliance Report',
    description: 'Audit-chain integrity per service, retention status, and operator-level event volume.',
    category: 'Compliance',
  },
  {
    slug: 'reconciliation',
    reportKey: 'finance.reconciliation.v1',
    title: 'Reconciliation Report',
    description: 'POS, transfer, bill-payment, and cash reconciliation against ledger settlement.',
    category: 'Finance',
  },
  {
    slug: 'revenue-sharing',
    reportKey: 'franchise.revenue-share.v1',
    title: 'Revenue Sharing Statement',
    description: 'Per-franchise commission breakdown by service type with upcoming remittance schedule.',
    category: 'Franchise',
  },
  {
    slug: 'staff-productivity',
    reportKey: 'staff.productivity.v1',
    title: 'Staff Productivity Report',
    description:
      'Per-operator transaction volume, average handle time, and SLA conformance across services.',
    category: 'Operations',
  },
  {
    slug: 'gov-services-monthly',
    reportKey: 'government.monthly.v1',
    title: 'Government Services Monthly',
    description: 'MDA service volumes, fees collected, and remittance status for the calendar month.',
    category: 'Government',
  },
  {
    slug: 'franchise-scorecard',
    reportKey: 'franchise.scorecard.v1',
    title: 'Franchise Scorecard',
    description: 'Tier-weighted KPIs, performance trend, incentive eligibility, and compliance status.',
    category: 'Franchise',
  },
];

export function findPreBuiltReport(slug: string): PreBuiltReport | undefined {
  return PREBUILT_REPORTS.find((r) => r.slug === slug);
}

// ---------------------------------------------------------------------------
// runPreBuiltReport — maps the legacy v1-style reportKey to the purpose-built
// `GET /v1/reports/<slug>` endpoint.
// ---------------------------------------------------------------------------

/**
 * Wire-level path segment (after `/v1/reports/`) for each reportKey.
 *
 * Backend: `apps/service-reporting-api/src/modules/reports/pre-built-reports.controller.ts`.
 */
export const PRE_BUILT_PATH_BY_KEY: Readonly<Record<string, string>> = {
  'daily.transactions.summary.v1': 'daily-transactions',
  'delivery.sla.v1': 'delivery-sla',
  'fleet.operations.v1': 'fleet-ops',
  'kyc.compliance.v1': 'kyc-compliance',
  'audit.compliance.v1': 'audit-compliance',
  'finance.reconciliation.v1': 'reconciliation',
  'franchise.revenue-share.v1': 'revenue-sharing',
  'staff.productivity.v1': 'staff-productivity',
  'government.monthly.v1': 'gov-services-monthly',
  'franchise.scorecard.v1': 'franchise-scorecard',
  'customer.overview.v1': 'customer-overview',
};

export interface PreBuiltRunResult {
  readonly reportKey: string;
  readonly fromDate: string;
  readonly toDate: string;
  readonly generatedAt: string;
  /** Raw response payload from the purpose-built endpoint. */
  readonly data: Record<string, unknown>;
}

export interface RunPreBuiltParams {
  readonly fromDate: string;
  readonly toDate: string;
}

/**
 * Resolve a v1-style `reportKey` to its purpose-built endpoint and call it
 * with the operator-supplied date range. The pre-built controller exposes a
 * mix of `date` / `from`+`to` / `period` / `month` query params; we pass
 * whichever the endpoint accepts (sending an unknown query is a no-op on the
 * NestJS controllers — they only read the params they declare).
 */
export async function runPreBuiltReport(
  reportKey: string,
  params: RunPreBuiltParams,
): Promise<PreBuiltRunResult> {
  const slug = PRE_BUILT_PATH_BY_KEY[reportKey];
  if (!slug) {
    throw new Error(`Unknown pre-built reportKey: ${reportKey}`);
  }

  // Build a query object that satisfies every endpoint variant:
  //   - daily-transactions: date=YYYY-MM-DD (we send the `to` date)
  //   - delivery-sla: from + to
  //   - fleet-ops / kyc-compliance / audit-compliance / reconciliation /
  //     revenue-sharing / staff-productivity / franchise-scorecard: period
  //     (we send "CUSTOM" + from/to so the service can honour the range when
  //     it adopts custom periods; today these endpoints fall back to their
  //     defaults if `period` is unrecognised).
  //   - gov-services-monthly: month=YYYY-MM (derived from `to`)
  const query: Record<string, string> = {
    date: params.toDate,
    from: params.fromDate,
    to: params.toDate,
    period: 'CUSTOM',
    month: params.toDate.slice(0, 7),
  };

  const data = await request<Record<string, unknown>>(
    apiBase(),
    `/v1/reports/${slug}`,
    { method: 'GET', query },
  );

  return {
    reportKey,
    fromDate: params.fromDate,
    toDate: params.toDate,
    generatedAt:
      typeof data['generatedAt'] === 'string'
        ? (data['generatedAt'] as string)
        : new Date().toISOString(),
    data,
  };
}

// ---------------------------------------------------------------------------
// customer.overview.v1 — typed helper for the staff `/admin/customers/dashboard`
// page. Hits GET /v1/reports/customer-overview directly (the generic
// runPreBuiltReport wrapper would fold the response inside a `.data` envelope).
// ---------------------------------------------------------------------------

export interface CustomerOverviewByState {
  readonly stateId: number | null;
  readonly stateName: string | null;
  readonly count: number;
}

export interface CustomerOverviewTopRow {
  readonly customerId: string;
  readonly customerNumber: string;
  readonly fullName: string;
  readonly orderCount: number;
  /** DECIMAL string (₦). */
  readonly totalNgn: string;
}

export interface CustomerOverviewReport {
  readonly reportKey: 'customer.overview.v1';
  readonly fromDate: string;
  readonly toDate: string;
  readonly generatedAt: string;
  readonly totalCustomers: number;
  readonly newInPeriod: number;
  readonly byGrade: Readonly<{
    WALK_IN: number;
    REGISTERED: number;
  }> &
    Readonly<Record<string, number>>;
  readonly byState: readonly CustomerOverviewByState[];
  readonly topByRevenue: readonly CustomerOverviewTopRow[];
  readonly topByOrders: readonly CustomerOverviewTopRow[];
  /** True when the upstream surface (orders / revenue) failed and the report
   *  has fallen back to placeholder zeros. */
  readonly stub?: boolean;
}

export interface RunCustomerOverviewParams {
  readonly fromDate: string;
  readonly toDate: string;
  readonly grade?: string;
  readonly geoStateId?: number;
}

export async function runCustomerOverviewReport(
  params: RunCustomerOverviewParams,
): Promise<CustomerOverviewReport> {
  return request<CustomerOverviewReport>(apiBase(), '/v1/reports/customer-overview', {
    method: 'GET',
    query: {
      fromDate: params.fromDate,
      toDate: params.toDate,
      grade: params.grade,
      geoStateId: params.geoStateId,
    },
  });
}
